import { createContext, useContext, type ReactNode } from "react";
import { NotificationProvider } from "@platform/hooks";

import { AddDocumentsDialogProvider } from "./AddDocumentsDialogContext";
import { ConfirmationDialogProvider } from "./ConfirmationDialogContext";
import { MultipleLocProvider } from "./MultipleLocContext";
import { RenameDocumentProvider } from "./RenameDocumentContext";
import { CoverageSummaryProvider } from "./CoverageSummaryContext";
import { DocumentFiltersProvider } from "./DocumentFiltersContext";
import { DocumentSelectionProvider } from "./DocumentSelectionContext";
import { ErrorProvider } from "./ErrorContext";
import { RfpIdProvider } from "./RfpIdContext";

const PlanEffectiveDateContext = createContext<string | undefined>(undefined);

export const usePlanEffectiveDate = (): string | undefined =>
  useContext(PlanEffectiveDateContext);

export type DocumentLibraryProviderProps = {
  rfpId: string;
  brokerId: number;
  brokerPortalRfpId: number;
  carriers: string[];
  planEffectiveDate?: string;
  children: ReactNode;
};

export const DocumentLibraryProvider = ({
  rfpId,
  brokerId,
  brokerPortalRfpId,
  carriers,
  planEffectiveDate,
  children,
}: DocumentLibraryProviderProps): React.JSX.Element => (
  <PlanEffectiveDateContext.Provider value={planEffectiveDate}>
    <NotificationProvider>
      <ErrorProvider>
        <RfpIdProvider rfpId={rfpId}>
          <ConfirmationDialogProvider>
            <MultipleLocProvider>
              <RenameDocumentProvider>
                <DocumentSelectionProvider>
                  <CoverageSummaryProvider rfpId={rfpId}>
                    <DocumentFiltersProvider>
                      <AddDocumentsDialogProvider
                        rfpId={rfpId}
                        brokerId={brokerId}
                        brokerPortalRfpId={brokerPortalRfpId}
                        carriers={carriers}
                        planEffectiveDate={planEffectiveDate}
                      >
                        {children}
                      </AddDocumentsDialogProvider>
                    </DocumentFiltersProvider>
                  </CoverageSummaryProvider>
                </DocumentSelectionProvider>
              </RenameDocumentProvider>
            </MultipleLocProvider>
          </ConfirmationDialogProvider>
        </RfpIdProvider>
      </ErrorProvider>
    </NotificationProvider>
  </PlanEffectiveDateContext.Provider>
);
